import type { GetServerSideProps, NextPage } from 'next'
import dynamic from 'next/dynamic'
import { unstable_serialize } from 'swr'
import { client } from '~/api/client'
import { getWebShopsSearchV1 } from '~/api/gen'
import { Layout } from '~/components/Layout'
import { Meta } from '~/components/Meta'
import { FETCH_COUNT_PER_PAGE, ShopList } from '~/features/ShopList/ShopList'
import { convertNormalizedConditionsToParameters } from '~/features/ShopList/lib/convertNormalizedConditionsToParameters'
import { getBreadcrumb } from '~/features/ShopList/lib/getBreadcrumb'
import { getMetaDescription } from '~/features/ShopList/lib/getMetaDescription'
import { getTitle } from '~/features/ShopList/lib/getTitle'
import { wearUrl } from '~/features/shared/domain/wearUrl'
import { normalizeBrand } from '~/features/shared/search/normalizer/brand'
import { buildShopListUrl } from '~/features/shared/search/shop/buildShopListUrl'
import { getSearchShopsKey } from '~/features/shared/search/shop/lib/getSearchShopsKey'
import { parseUrl } from '~/features/shared/search/shop/parseUrl'
import { NormalizedConditions } from '~/features/shared/search/shop/types'
import { assertSuccess } from '~/lib/assertSuccess'
import { ITEM_LIST_HEADER_ADJUST_URL } from '~/lib/constants'
import { getPageNumberFromQueryValue } from '~/lib/getPageNumberFromQueryValue'
import { sendGtmEvOnClickDownloadHeader } from '~/lib/sendGtmEvOnClickDownloadHeader'

const GoogleAdScripts = dynamic(
  () => import('~/features/shared/ad/components/GoogleAdScripts'),
  { ssr: false }
)

type Props = {
  conditions: NormalizedConditions
  pageNumber: number
}

// eslint-disable-next-line complexity
export const getServerSideProps: GetServerSideProps<Props> = async ({
  resolvedUrl,
  query,
}) => {
  const { pageNumber, isValid } = getPageNumberFromQueryValue(query.pageno)

  const url = wearUrl(resolvedUrl)
  const searchConditions = parseUrl(url)

  if (!isValid || url.searchParams.get('pageno') === '1') {
    return {
      redirect: {
        destination: buildShopListUrl({
          ...searchConditions,
          additionalQueries: searchConditions?.additionalQueries?.filter(
            ([key]) => key !== 'pageno'
          ),
        }),
        permanent: true,
      },
    }
  }

  const normalizedBrand = await normalizeBrand(searchConditions.brand)
  const normalizedConditions = {
    ...searchConditions,
    sort: searchConditions.sort ?? 'popular',
    shopType: searchConditions.shopType ?? 'all',
    brand: normalizedBrand,
  }

  const searchParams = convertNormalizedConditionsToParameters(
    normalizedConditions,
    {
      pagesize: FETCH_COUNT_PER_PAGE,
    }
  )
  const searchKey = getSearchShopsKey(searchParams)
  const searchRes = await getWebShopsSearchV1(client, {
    parameter: searchParams,
  })

  const pagenoOmittedUrl = buildShopListUrl({
    ...normalizedConditions,
    additionalQueries: normalizedConditions?.additionalQueries?.filter(
      ([key]) => key !== 'pageno'
    ),
  })

  if (searchRes.type === 'error' && searchRes.raw.status === 400) {
    //存在しないpagenoでかつ、pagesize * pagenoが10000を超える場合は400エラーが返るので、pagenoパラメータを排除
    //https://st-zozo.slack.com/archives/CDVG57XL2/p1747805638949789?thread_ts=1747804982.039919&cid=CDVG57XL2
    return {
      redirect: {
        destination: pagenoOmittedUrl,
        permanent: true,
      },
    }
  }
  assertSuccess(searchRes)

  //存在しないpagenoでかつ、pagesize * pagenoが10000以内の場合はAPIから空の配列が返る
  //totalPageが1以上でかつ存在しないpagenoで、かつ空の配列の場合はpagenoパラメータを排除
  const totalPage = Math.ceil(
    Math.min(searchRes.data.total_count, searchRes.data.max_count) /
      FETCH_COUNT_PER_PAGE
  )
  if (
    totalPage > 0 &&
    pageNumber > totalPage &&
    searchRes.data.shops.length === 0
  )
    return {
      redirect: {
        destination: pagenoOmittedUrl,
        permanent: true,
      },
    }

  return {
    props: {
      fallback: {
        [unstable_serialize(searchKey)]: searchRes.data,
      },
      conditions: JSON.parse(JSON.stringify(normalizedConditions)),
      pageNumber,
    },
  }
}

const ShopListPage: NextPage<Props> = ({ conditions, pageNumber }) => {
  const conditionsWithBrandName = {
    shopType: conditions.shopType,
    brandName: conditions.brand?.name,
    searchWord: conditions.searchWord,
  }
  return (
    <Layout
      breadcrumbList={getBreadcrumb(conditionsWithBrandName)}
      headerProps={{
        hideSearchIcon: true,
        initialSearchWord: conditions.searchWord ?? '',
        searchType: 'shop',
      }}
      appDownloadUrl={ITEM_LIST_HEADER_ADJUST_URL} //TODO:ショップ一覧のAdjustUrlをいただいたら差し替える
      abstractUrl="wear.jp/shop/"
      onClickAppLink={sendGtmEvOnClickDownloadHeader}
    >
      <GoogleAdScripts pages={[{ label: 'sp:shop' }, { label: 'pc:shop' }]} />
      <Meta
        title={getTitle({ ...conditionsWithBrandName, pageNumber })}
        description={getMetaDescription(conditionsWithBrandName)}
        url={wearUrl(buildShopListUrl(conditions)).href}
        appUrl={undefined}
        robots //TODO:API連携時に動的に設定する
      />
      <ShopList conditions={conditions} pageNumber={pageNumber} />
    </Layout>
  )
}
export default ShopListPage
